import { Injectable, inject, signal } from '@angular/core';
import { classes } from 'polytype';
import type { Observable } from 'rxjs';

import type {
    AgentLocation,
    CodeRepoAdapter,
    CommitChangesPayload,
    GitBranch,
    GitCommitDetail,
    GitFile,
    GitFileList,
    GitLogCommit,
    GitTree,
    RegisteredAgent,
    RepoAgent,
    RepoAgentList,
    TemplateCatalog,
} from '@app/assets/code-repo/code-repo.model';
import { CODE_REPO_PATH } from '@app/shared/api/api-route.config';
import { AppConfigService } from '@app/shared/api/app-config.service';
import type { ItemResponse, ListResponse } from '@app/shared/api/crud.service';
import { WithCrudService } from '@app/shared/api/crud.service';
import { RestService } from '@app/shared/api/rest.service';
import { ToastNotificationService } from '@app/shared/notification/toast-notification.service';

@Injectable({
    providedIn: 'root',
})
export class CodeRepoService extends classes(WithCrudService<CodeRepoAdapter>) {
    protected endpoint = CODE_REPO_PATH;

    protected readonly api = inject(RestService);
    protected readonly toast = inject(ToastNotificationService);
    private readonly appConfigService = inject(AppConfigService);

    // The Code Repo currently open in the registry. Its browser and commit pages read it from here.
    readonly item = signal<CodeRepoAdapter>(null);

    // The browser shows its own error for a missing branch, path or commit, so these skip the global error toast
    getBranches(id: number): Observable<ListResponse<GitBranch>> {
        return this.api.get<ListResponse<GitBranch>>(`${this.endpoint}${id}/branches`, false);
    }

    getRemote(id: number): Observable<ItemResponse<{ url: string | null }>> {
        return this.api.get<ItemResponse<{ url: string | null }>>(
            `${this.endpoint}${id}/remote`,
            false,
        );
    }

    syncFromGithub(id: number): Observable<ItemResponse<{ status: string }>> {
        return this.api.post<ItemResponse<{ status: string }>>(
            `${this.endpoint}${id}/sync`,
            {},
            false,
        );
    }

    getTemplateCatalog(): Observable<ItemResponse<TemplateCatalog>> {
        return this.api.get<ItemResponse<TemplateCatalog>>(`${this.endpoint}templates`);
    }

    cloneFromGithub(payload: {
        projectSlug: string;
        name: string;
        installationId: number;
        repositoryId: number;
    }): Observable<ItemResponse<CodeRepoAdapter>> {
        return this.api.post<ItemResponse<CodeRepoAdapter>>(
            `${this.endpoint}clone/github`,
            payload,
            false,
        );
    }

    // Commits a starter template's files to the Code Repo's default branch
    addTemplate(
        id: number,
        template: { usecase: string; llm: string; framework: string },
    ): Observable<ItemResponse<{ commitSha: string }>> {
        return this.api.post<ItemResponse<{ commitSha: string }>>(
            `${this.endpoint}${id}/template`,
            template,
            false,
        );
    }

    createBranch(id: number, name: string, fromRef: string): Observable<ItemResponse<GitBranch>> {
        return this.api.post<ItemResponse<GitBranch>>(
            `${this.endpoint}${id}/branches`,
            { name, fromRef },
            false,
        );
    }

    getTree(id: number, ref: string, path: string): Observable<ItemResponse<GitTree>> {
        return this.api.getWithParams<ItemResponse<GitTree>>(
            `${this.endpoint}${id}/tree`,
            { ref, path },
            false,
        );
    }

    getFile(id: number, ref: string, path: string): Observable<ItemResponse<GitFile>> {
        return this.api.getWithParams<ItemResponse<GitFile>>(
            `${this.endpoint}${id}/blob`,
            { ref, path },
            false,
        );
    }

    // Every directory and file at a branch or commit, for the file tree beside the browser
    getFiles(id: number, ref: string): Observable<ItemResponse<GitFileList>> {
        return this.api.getWithParams<ItemResponse<GitFileList>>(
            `${this.endpoint}${id}/files`,
            { ref },
            false,
        );
    }

    // The agents defined in the code of a branch or commit, found by reading the files without running them, with the
    // GGX agents imported from each. It is asked for each time, as agents can be imported from anywhere at any time.
    getAgents(id: number, ref: string): Observable<ItemResponse<RepoAgentList>> {
        return this.api.getWithParams<ItemResponse<RepoAgentList>>(
            `${this.endpoint}${id}/agents`,
            { ref },
            false,
        );
    }

    // The file and line `from <module> import <attribute>` finds an agent at, on a branch or commit
    getAgentLocation(
        id: number,
        ref: string,
        agent: { sourceRoot: string; module: string; attribute: string },
    ): Observable<ItemResponse<AgentLocation>> {
        return this.api.getWithParams<ItemResponse<AgentLocation>>(
            `${this.endpoint}${id}/agents/location`,
            { ref, sourceRoot: agent.sourceRoot, module: agent.module, attribute: agent.attribute },
            false,
        );
    }

    // The agent keeps a reference to the branch, so later commits reach it without registering it again.
    // `name` and `alias` are the GGX agent's, chosen by the user so that agents of several repositories don't clash.
    registerAgent(
        id: number,
        branch: string,
        agent: RepoAgent,
        name: string,
        alias: string,
    ): Observable<ItemResponse<RegisteredAgent>> {
        return this.api.post<ItemResponse<RegisteredAgent>>(
            `${this.endpoint}${id}/agents`,
            {
                branch,
                name,
                alias,
                attribute: agent.name,
                module: agent.module,
                sourceRoot: agent.sourceRoot,
            },
            false,
        );
    }

    // Adds, changes and deletes files in one commit, and pushes it when the repository is linked to GitHub
    commitChanges(
        id: number,
        payload: CommitChangesPayload,
    ): Observable<ItemResponse<{ commitSha: string }>> {
        return this.api.post<ItemResponse<{ commitSha: string }>>(
            `${this.endpoint}${id}/commits`,
            payload,
            false,
        );
    }

    // The newest commits of a branch, children before their parents, for the history graph
    getCommits(id: number, ref: string, limit = 200): Observable<ListResponse<GitLogCommit>> {
        return this.api.getWithParams<ListResponse<GitLogCommit>>(
            `${this.endpoint}${id}/commits`,
            { ref, limit },
            false,
        );
    }

    // What `git clone` uses: <server>/git/<project>/<name>.git, like a GitHub URL.
    // The username is part of it so that git only has to ask for the password.
    getCloneUrl(projectSlug: string, name: string, username: string): string {
        const { backendUrl } = this.appConfigService.appConfig();
        const url = new URL(
            `${backendUrl ?? ''}/git/${encodeURIComponent(projectSlug)}/${encodeURIComponent(name)}.git`,
            window.location.origin,
        );
        url.username = username;
        return url.href;
    }

    getCommit(id: number, sha: string): Observable<ItemResponse<GitCommitDetail>> {
        return this.api.get<ItemResponse<GitCommitDetail>>(
            `${this.endpoint}${id}/commits/${sha}`,
            false,
        );
    }
}
